import { useSyncExternalStore } from 'react';

// Theme for every InboxKit surface: "system" (default), "light" (paper), or
// "dark" (ink). Sets <html data-theme>; brand.css and the app tokens follow it.
// Each index.html sets the attribute before first paint with the same rule.
// Safe to import during pre-rendering (no DOM): it then reports system/light.

export type ThemePreference = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'ik-theme';
const listeners = new Set<() => void>();
const hasDom = typeof window !== 'undefined';
const media = hasDom ? window.matchMedia('(prefers-color-scheme: dark)') : undefined;

function readPreference(): ThemePreference {
  if (!hasDom) return 'system';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system'; // storage blocked (privacy mode)
  }
}

let preference = readPreference();

function resolve(): Theme {
  if (preference === 'system') return media?.matches ? 'dark' : 'light';
  return preference;
}

function apply() {
  if (!hasDom) return;
  document.documentElement.dataset.theme = resolve();
  for (const listener of listeners) listener();
}

media?.addEventListener('change', apply);
apply();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    if (next === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // storage blocked: the choice still applies for this page
  }
  apply();
}

// Hydration renders these first, then React re-renders with the real values.
const serverTheme = (): Theme => 'light';
const serverPreference = (): ThemePreference => 'system';

/** Resolved theme plus the stored preference. */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, resolve, serverTheme);
  const pref = useSyncExternalStore(subscribe, () => preference, serverPreference);
  return { theme, preference: pref };
}

/** The theme already on <html> (set pre-paint), for code outside render. */
export function currentTheme(): Theme {
  return hasDom && document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}
